/**
 * ShadowPass — creatures and reef casting sun shadows onto the sand.
 *
 * A hand-rolled single-cascade shadow map rather than three's, because every material in
 * this scene is a custom ShaderMaterial with hand-rolled lighting and `lights: false`;
 * opting into three's shadow machinery would mean threading its chunks through all of them.
 * Here exactly one surface receives (the seafloor), so one depth render and one comparison
 * is the whole feature.
 *
 * Casters render with the depth twin of their own SharkMaterial, which shares the parent's
 * uniform object — so a shark's shadow undulates, bends and opens its jaw in lockstep with
 * the shark. A plain depth override would cast the bind pose.
 */
import * as THREE from 'three';
import { OCEAN, WATER } from '../data/tuning.ts';

/** world units across the shadow frustum; it rides with the player, so this is a radius budget */
const EXTENT = 62;
const SIZE = 2048;
/** meshes on this layer are shadow casters; the main camera still renders layer 0 as usual */
const CASTER_LAYER = 1;

/**
 * Anything that can hand over a depth twin sharing its own uniforms can cast. Structural
 * rather than a class check on purpose: the sharks, the reef and the octopus are three
 * different materials with three different vertex programs, and every one of them has to
 * cast the shadow of its DEFORMED pose, not of its bind pose.
 */
type Caster = THREE.Material & { makeShadowMaterial(): THREE.ShaderMaterial };
function canCast(m: THREE.Material | THREE.Material[]): m is Caster {
  return !Array.isArray(m) && typeof (m as Partial<Caster>).makeShadowMaterial === 'function';
}

export class ShadowPass {
  readonly target: THREE.WebGLRenderTarget;
  /** light-space viewProjection, for the receiver to project into */
  readonly matrix = new THREE.Matrix4();

  private readonly camera: THREE.OrthographicCamera;
  private readonly lightDir = new THREE.Vector3();
  private readonly focus = new THREE.Vector3();
  /** caster -> its depth twin; built lazily and reused */
  private readonly twins = new WeakMap<THREE.Material, THREE.ShaderMaterial>();
  private readonly swapped: Array<{ mesh: THREE.Mesh | THREE.InstancedMesh; mat: THREE.Material | THREE.Material[] }> = [];

  constructor() {
    const depth = new THREE.DepthTexture(SIZE, SIZE);
    depth.format = THREE.DepthFormat;
    depth.type = THREE.UnsignedIntType;
    depth.minFilter = THREE.NearestFilter;
    depth.magFilter = THREE.NearestFilter;

    this.target = new THREE.WebGLRenderTarget(SIZE, SIZE, {
      depthTexture: depth,
      depthBuffer: true,
      stencilBuffer: false,
    });
    this.target.texture.name = 'shadow';

    this.camera = new THREE.OrthographicCamera(-EXTENT, EXTENT, EXTENT, -EXTENT, 1, 240);
    // Casters opt in by enabling CASTER_LAYER; the shadow camera sees nothing else. Without
    // this the seafloor renders into its own shadow map and self-shadows every dune.
    this.camera.layers.set(CASTER_LAYER);

    // WATER.sunDir points TOWARD the sun, so the light travels along its negation.
    this.lightDir.set(WATER.sunDir[0], WATER.sunDir[1], WATER.sunDir[2]).normalize();
    if (this.lightDir.y < 0) this.lightDir.negate();
  }

  /**
   * @param casters meshes to render into the depth buffer. The seafloor is the receiver and
   *                must not be in this list, or it self-shadows across every dune.
   */
  render(
    gl: THREE.WebGLRenderer,
    scene: THREE.Scene,
    casters: Array<THREE.Mesh | THREE.InstancedMesh>,
    focusX: number,
    focusZ: number,
  ): void {
    // Snap the frustum to a grid so the shadow texels do not crawl as the player moves —
    // an unsnapped ortho shadow shimmers along every edge, which on a calm reef is the most
    // distracting artefact there is.
    const step = (EXTENT * 2) / SIZE * 16;
    const fx = Math.round(focusX / step) * step;
    const fz = Math.round(focusZ / step) * step;
    this.focus.set(fx, OCEAN.surfaceHeight * 0.35, fz);

    this.camera.position.copy(this.focus).addScaledVector(this.lightDir, 110);
    this.camera.lookAt(this.focus);
    this.camera.updateMatrixWorld(true);
    this.camera.updateProjectionMatrix();
    this.matrix.multiplyMatrices(this.camera.projectionMatrix, this.camera.matrixWorldInverse);

    // Swap in the depth twins, render, put everything back.
    this.swapped.length = 0;
    for (const mesh of casters) {
      const mat = mesh.material;
      if (!canCast(mat)) continue;
      let twin = this.twins.get(mat);
      if (twin === undefined) {
        twin = mat.makeShadowMaterial();
        this.twins.set(mat, twin);
      }
      this.swapped.push({ mesh, mat });
      mesh.material = twin;
      mesh.layers.enable(CASTER_LAYER);
    }
    if (this.swapped.length === 0) return;

    const prevTarget = gl.getRenderTarget();
    gl.setRenderTarget(this.target);
    gl.clear(true, true, false);
    gl.render(scene, this.camera);
    gl.setRenderTarget(prevTarget);

    for (const s of this.swapped) s.mesh.material = s.mat;
    this.swapped.length = 0;
  }

  /**
   * Point the light camera at wherever the sun (or moon) now is. Without this the day cycle
   * changes every colour in the scene while every shadow stays where it was at noon, which
   * reads as broken faster than no shadows at all.
   */
  setSunDir(v: { x: number; y: number; z: number }): void {
    this.lightDir.set(v.x, v.y, v.z).normalize();
    if (this.lightDir.y < 0) this.lightDir.negate();
  }

  dispose(): void {
    this.target.depthTexture?.dispose();
    this.target.dispose();
  }
}
